import { useMemo } from "react";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import type { PlatformComponentProps } from "@b2bportal/core-types";
import {
  type HotelItinerarySummary,
  ReservationStateEnum,
} from "@b2bportal/lodging-api";
import { DateTimeFormatStyle, useI18nContext } from "@hopper-b2b/i18n";
import { TripsCard } from "@tenant/trips";
import { Icon, Pill, PillTone } from "@tenant/ui";
import clsx from "clsx";
import dayjs from "dayjs";
import { useHotelReservationCard } from "./hooks";
import styles from "./styles.module.scss";

export interface HotelReservationCardComponentProps
  extends PlatformComponentProps {
  reservation: HotelItinerarySummary;
  selectItinerary: () => void;
  showViewDetails?: boolean;
  showBookingId?: boolean;
}

export const HotelReservationCard = (
  props: HotelReservationCardComponentProps
) => {
  const [block, cn] = useModuleBEM(styles, "HotelReservationCard");
  const { t, formatDateTime } = useI18nContext();

  const {
    className,
    children,
    showViewDetails = true,
    showBookingId = true,
  } = props;
  const { context, handlers, text } = useHotelReservationCard(props);

  const checkInDate = formatDateTime(
    dayjs(context.checkInDate).toDate(),
    DateTimeFormatStyle.ShortMonthDayShortWeekday
  );

  const checkOutDate = formatDateTime(
    dayjs(context.checkOutDate).toDate(),
    DateTimeFormatStyle.ShortMonthDayShortWeekday
  );

  const pill = useMemo(() => {
    switch (context.state) {
      case ReservationStateEnum.Cancelled:
        return (
          <Pill tone={PillTone.error}>
            {t("trips.tripCard.status.canceled")}
          </Pill>
        );
      case ReservationStateEnum.CancellationFailure:
        return (
          <Pill tone={PillTone.error}>
            {t("trips.tripCard.status.cancellationFailure")}
          </Pill>
        );
      case ReservationStateEnum.Pending:
      case ReservationStateEnum.Confirmed:
        return null;
    }
  }, [context.state, t]);

  return (
    <TripsCard
      showViewDetails={showViewDetails}
      showBookingId={showBookingId}
      bookingId={props.reservation.reservationId}
      className={clsx(block, className)}
      product={
        <>
          <Icon
            iconName={IconNameEnum.hotelReservation}
            className={cn("product-icon")}
          />{" "}
          {text.product}
        </>
      }
      title={text.title}
      status={pill}
      onClick={handlers.selectItinerary}
    >
      <div className={cn("product-info")}>
        <div className={cn("stay-data")}>
          <div className={cn("location")}>{text.location}</div>
          <div className={cn("dates-container")}>
            <div className={cn("date")}>
              <span className={cn("date-label")}>{text.checkInDate}:</span>{" "}
              {checkInDate}
            </div>
            <div className={cn("date")}>
              <span className={cn("date-label")}>{text.checkOutDate}:</span>{" "}
              {checkOutDate}
            </div>
          </div>
        </div>
      </div>
      {children}
    </TripsCard>
  );
};
